import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Skeleton } from '@/shared/components';
import { toast } from "sonner";

import { supabaseService, type Repository } from '@/shared/services/supabaseService';
import RepositorySidebar from '@/features/repository/components/RepositorySidebar';
import RepositoryRulesManager from '@/features/rules/components/RepositoryRulesManager';
import PullRequestList from '@/features/pull-request/components/PullRequestList';
import { DashboardLayout } from '@/features/dashboard/components/DashboardLayout';
import { Section } from '@/ui/layout/Section';
import { GeneralSettings } from '../components/GeneralSettings';
import { FrequencySettings } from '../components/FrequencySettings';
import { RepositoryHeader } from '../components/RepositoryHeader';

const RepositoryDetailsPage: React.FC = () => {
  const { repoId } = useParams<{ repoId: string; prNumber?: string }>();

  const [repository, setRepository] = useState<Repository | null>(null);
  const [reviewEnabled, setReviewEnabled] = useState(true);
  const [includeSummary, setIncludeSummary] = useState(false);
  const [autoReviewEnabled, setAutoReviewEnabled] = useState(false);
  const [scanFrequency, setScanFrequency] = useState<'per_pr' | 'per_commit'>('per_pr');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('pull_requests'); // Default to settings tab

  useEffect(() => {
    const fetchRepositoryDetails = async () => {
      if (!repoId) return;
      setLoading(true);
      try {
        const repoData = await supabaseService.getRepositoryById(repoId);
        if (repoData) {
          setRepository(repoData);

          setReviewEnabled(repoData.review_enabled ?? true);
          setIncludeSummary(repoData.include_summary ?? false);
          setAutoReviewEnabled(repoData.auto_review_enabled ?? false);
          setScanFrequency(repoData.scan_frequency || 'per_pr');
        } else {
          setError('Repository not found.');
        }
      } catch {
        setError('Failed to load repository details.');
      } finally {
        setLoading(false);
      }
    };
    fetchRepositoryDetails();
  }, [repoId]);

  const handleSave = async () => {
    if (!repoId) return;
    setSaving(true);
    try {
      await supabaseService.updateRepositorySettings(repoId, {
        reviewEnabled,
        includeSummary,
        autoReviewEnabled,
        scanFrequency
      });
      toast.success("Repository settings saved successfully.");
    } catch {
      toast.error("Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  const handleTabChange = (tab: string) => {
      window.location.href = `/dashboard?tab=${tab}`;
  }

  if (loading) {
    return (
        <DashboardLayout activeTab="repositories" onTabChange={handleTabChange}>
            <Section className="p-12">
                <Skeleton className="h-8 w-1/3 mb-4 rounded-none" />
                <Skeleton className="h-64 w-full rounded-none" />
            </Section>
        </DashboardLayout>
    );
  }

  return (
    <DashboardLayout activeTab="repositories" onTabChange={handleTabChange}>
      <Section className="p-8 md:p-12" label="REPO_CONTEXT">
        <RepositoryHeader
            repository={repository}
            activeTab={activeTab}
            saving={saving}
            onSave={handleSave}
            error={error}
        />
      </Section>

      <div className="border-b-2 border-neutral-950 dark:border-neutral-100 flex flex-col min-h-[500px]">
            {/* Top Navigation Bar */}
            <RepositorySidebar onSelect={setActiveTab} selectedKey={activeTab} />

            {/* Content Area */}
            <div className="flex-1 p-8 bg-background">
                {activeTab === 'pull_requests' && repoId && (
                    <div className="space-y-6">
                        <div className="flex items-center gap-2 mb-6">
                            <span className="w-2 h-2 bg-blue-500 rounded-none inline-block"></span>
                            <h3 className="font-mono text-sm font-bold uppercase text-muted-foreground">Pull Requests</h3>
                        </div>
                        <PullRequestList repoId={repoId} />
                    </div>
                )}

                {activeTab === 'instructions' && (
                    <div className="space-y-6">
                        <div className="flex items-center gap-2 mb-6">
                            <span className="w-2 h-2 bg-purple-500 rounded-none inline-block"></span>
                            <h3 className="font-mono text-sm font-bold uppercase text-muted-foreground">Rule Configuration</h3>
                        </div>
                        {repoId && (
                            <RepositoryRulesManager
                                repositoryId={repoId}
                            />
                        )}
                    </div>
                )}

                {activeTab === 'settings' && (
                    <>
                        <GeneralSettings
                            reviewEnabled={reviewEnabled}
                            setReviewEnabled={setReviewEnabled}
                            autoReviewEnabled={autoReviewEnabled}
                            setAutoReviewEnabled={setAutoReviewEnabled}
                            includeSummary={includeSummary}
                            setIncludeSummary={setIncludeSummary}
                        />
                        <FrequencySettings
                            scanFrequency={scanFrequency}
                            setScanFrequency={setScanFrequency}
                        />
                    </>
                )}
            </div>
      </div>
    </DashboardLayout>
  );
};

export default RepositoryDetailsPage;
